import { createRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { authedLayoutRoute } from './authed.js';
import { confirmDisbursement, createAdvance, migrateAdvance, forgiveAdvance, listAdvances, type AdvanceResponse } from '../api/advances.js';
import { createLoanConsentArtifact } from '../api/disbursementArtifacts.js';
import { ApproveForm, RejectForm } from './loansRequests.js';
import type { CurrencyCode } from '@kora/types';
import { formatDate, formatMoney } from '../lib/format.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';

type FormMode = 'new' | 'migration';

/**
 * Tags a pre-migration failure (client URL validation or consent-artifact
 * creation) so the migration mutation's onError can distinguish it from a
 * migration-endpoint ApiError (e.g. the Layer-2 concurrency 409).
 */
class MigrationFormError extends Error {
  constructor(message: string, readonly stage: 'validation' | 'artifact') {
    super(message);
    this.name = 'MigrationFormError';
  }
}

/**
 * /employees/$id/advances — HR-facing advance admin for one employee
 * (ADR-0009). Create loans, confirm disbursement, forgive, and review the
 * lifecycle. RBAC enforced by the API (owner|admin|hr).
 */
export const employeeAdvancesRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/employees/$id/advances',
  // ?mode=migration opens the create form in migration mode (loan-migration
  // feature). Anything else (incl. absent) defaults to a new loan.
  validateSearch: (search: Record<string, unknown>): { mode: FormMode } => ({
    mode: search.mode === 'migration' ? 'migration' : 'new',
  }),
  component: EmployeeAdvancesPage,
});

function EmployeeAdvancesPage(): JSX.Element {
  const { id } = employeeAdvancesRoute.useParams();
  const { mode } = employeeAdvancesRoute.useSearch();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['advances', id],
    queryFn: () => listAdvances({ employeeId: id }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['advances', id] });

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <nav className="text-xs text-gray-500">
        <Link to="/employees/$id" params={{ id }} className="hover:underline">Employee</Link> / Advances
      </nav>
      <h1 className="font-serif text-3xl">Employee advances</h1>

      <CreateAdvanceForm employeeId={id} initialMode={mode} onCreated={invalidate} />

      {isLoading ? <p className="text-gray-500">Loading...</p> : null}
      {data && data.advances.length === 0 ? (
        <p className="text-gray-400 text-sm">No advances for this employee yet.</p>
      ) : null}
      <div className="space-y-3">
        {data?.advances.map((a) => (
          <AdvanceRow key={a.id} advance={a} onChanged={invalidate} />
        ))}
      </div>
    </section>
  );
}

const inputCls = 'rounded border border-gray-800 bg-gray-950 px-2 py-1 text-sm text-gray-200';

function CreateAdvanceForm({ employeeId, initialMode, onCreated }: { employeeId: string; initialMode: FormMode; onCreated: () => void }): JSX.Element {
  const [mode, setMode] = useState<FormMode>(initialMode);
  const [principalAmount, setPrincipal] = useState('');
  const [currency, setCurrency] = useState('CDF');
  const [monthlyDeduction, setMonthly] = useState('');
  const [startPeriod, setStart] = useState('2026-06-01');
  const [consentArtifactId, setConsent] = useState('');
  // Migration-only fields
  const [remainingBalance, setRemaining] = useState('');
  const [originalDisbursementDate, setOriginalDate] = useState('');
  const [sourceSystem, setSource] = useState('');
  const [concurrencyOverrideReason, setOverride] = useState('');
  const [showOverride, setShowOverride] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reset = (): void => {
    setPrincipal(''); setMonthly(''); setRemaining(''); setOriginalDate(''); setSource('');
    setConsent(''); setOverride(''); setShowOverride(false); setErr(null);
  };

  const create = useMutation({
    mutationFn: () =>
      createAdvance({
        employeeId, kind: 'loan', principalAmount, currency, monthlyDeduction, startPeriod,
        ...(consentArtifactId ? { consentArtifactId } : {}),
      }),
    onSuccess: () => { reset(); onCreated(); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Create failed.'),
  });

  const migrate = useMutation({
    // Migration is a two-step flow (loan-migration consent fold-in): the
    // `consentArtifactId` field holds a Drive URL in migration mode — we
    // inline-create a loan_consent artifact from it, then submit the migration
    // with the new artifact id. The artifact step must succeed first; on its
    // failure we surface a clear error and DO NOT attempt the migration.
    mutationFn: async () => {
      const url = consentArtifactId.trim();
      if (!url.startsWith('https://')) {
        throw new MigrationFormError('Enter the consent document URL (must start with https://).', 'validation');
      }
      const dateStr = new Date().toISOString().slice(0, 10);
      // The form scope doesn't carry the employee's name, so the auto-filename
      // uses the employee id for a unique, identifiable Drive filename.
      const fileName = `loan-consent-migration-${employeeId}-${dateStr}.pdf`;
      let artifactId: string;
      try {
        const artifact = await createLoanConsentArtifact({ fileUrl: url, fileName, notes: `Migrated loan from ${sourceSystem}` });
        artifactId = artifact.id;
      } catch {
        throw new MigrationFormError('Failed to save the consent document. Verify the URL and try again.', 'artifact');
      }
      return migrateAdvance({
        employeeId, kind: 'loan', principalAmount, currency, monthlyDeduction, startPeriod,
        remainingBalance, originalDisbursementDate, sourceSystem, consentArtifactId: artifactId,
        ...(concurrencyOverrideReason ? { concurrencyOverrideReason } : {}),
      });
    },
    onSuccess: () => { reset(); onCreated(); },
    onError: (e) => {
      // Client validation / artifact-creation failure (no migration attempted).
      if (e instanceof MigrationFormError) {
        setErr(e.message);
        return;
      }
      // A Layer-2 concurrency 409 (without an override) → reveal the override field.
      if (e instanceof ApiError && e.status === 409 && /concurrent|one_active|one_per_kind|active advance/i.test(e.message) && !concurrencyOverrideReason) {
        setShowOverride(true);
        setErr('This employee already has an active/pending loan. Provide an override reason to migrate anyway.');
      } else {
        setErr(e instanceof ApiError ? e.message : 'Migration failed.');
      }
    },
  });

  const isMigration = mode === 'migration';
  const pending = create.isPending || migrate.isPending;

  return (
    <form
      className="rounded border border-gray-900 p-4 space-y-3"
      onSubmit={(e) => { e.preventDefault(); setErr(null); (isMigration ? migrate : create).mutate(); }}
    >
      <div className="flex items-center gap-4 text-sm">
        <label className="flex items-center gap-1 text-gray-300">
          <input type="radio" name="advance-mode" checked={!isMigration} onChange={() => { setMode('new'); setErr(null); }} /> New loan
        </label>
        <label className="flex items-center gap-1 text-gray-300">
          <input type="radio" name="advance-mode" checked={isMigration} onChange={() => { setMode('migration'); setErr(null); }} /> Migrate existing loan
        </label>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Field label="Principal"><input aria-label="Principal" type="text" inputMode="decimal" value={principalAmount} onChange={(e) => setPrincipal(e.target.value)} className={inputCls} /></Field>
        <Field label="Currency"><input aria-label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)} className={inputCls} /></Field>
        <Field label="Monthly deduction"><input aria-label="Monthly deduction" type="text" inputMode="decimal" value={monthlyDeduction} onChange={(e) => setMonthly(e.target.value)} className={inputCls} /></Field>
        <Field label="Start period"><input aria-label="Start period" value={startPeriod} onChange={(e) => setStart(e.target.value)} className={inputCls} /></Field>
        <Field label={isMigration ? 'Consent document URL' : 'Consent artefact ID'}>
          <input
            aria-label={isMigration ? 'Consent document URL' : 'Consent artefact ID'}
            value={consentArtifactId}
            onChange={(e) => setConsent(e.target.value)}
            placeholder={isMigration ? 'https://drive.google.com/…' : ''}
            className={inputCls}
          />
        </Field>
      </div>

      {isMigration ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 border-t border-gray-900 pt-3">
          <Field label="Current outstanding balance"><input aria-label="Current outstanding balance" type="text" inputMode="decimal" value={remainingBalance} onChange={(e) => setRemaining(e.target.value)} className={inputCls} /></Field>
          <Field label="Original disbursement date"><input aria-label="Original disbursement date" type="date" value={originalDisbursementDate} onChange={(e) => setOriginalDate(e.target.value)} className={inputCls} /></Field>
          <Field label="Source system"><input aria-label="Source system" placeholder="Sage Payroll" value={sourceSystem} onChange={(e) => setSource(e.target.value)} className={inputCls} /></Field>
          {showOverride ? (
            <Field label="Concurrency override reason"><input aria-label="Concurrency override reason" value={concurrencyOverrideReason} onChange={(e) => setOverride(e.target.value)} className={inputCls} /></Field>
          ) : null}
        </div>
      ) : null}

      {err ? <Alert variant="error">{err}</Alert> : null}
      <button type="submit" disabled={pending} className="rounded bg-white text-black px-4 py-2 text-sm font-medium disabled:opacity-50">
        {pending ? 'Saving…' : isMigration ? 'Migrate loan' : 'Create loan'}
      </button>
    </form>
  );
}

function AdvanceRow({ advance, onChanged }: { advance: AdvanceResponse; onChanged: () => void }): JSX.Element {
  const disburse = useMutation({ mutationFn: () => confirmDisbursement(advance.id), onSuccess: onChanged });
  const forgive = useMutation({ mutationFn: () => forgiveAdvance(advance.id, 'HR write-off'), onSuccess: onChanged });
  const [mode, setMode] = useState<'none' | 'approve' | 'reject'>('none');
  return (
    <div className="rounded border border-gray-900 p-4 space-y-3 text-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-white">{formatMoney(advance.principalAmount, advance.currency as CurrencyCode)} <span className="text-gray-500">· {advance.kind}</span></div>
          <div className="text-xs text-gray-500">balance {formatMoney(advance.remainingBalance, advance.currency as CurrencyCode)} · started {formatDate(advance.startPeriod)}</div>
          {advance.status === 'requested' && advance.requestPurpose ? (
            <div className="text-xs text-gray-400 mt-1">“{advance.requestPurpose}”</div>
          ) : null}
          {advance.status === 'rejected' && advance.rejectedReason ? (
            <div className="text-xs text-red-400 mt-1">Declined: {advance.rejectedReason}</div>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs rounded px-2 py-1 border border-gray-700 text-gray-300">{advance.status}</span>
          {advance.status === 'requested' && mode === 'none' ? (
            <>
              <button type="button" onClick={() => setMode('approve')} className="text-xs border border-gray-700 rounded px-2 py-1 hover:bg-gray-900">Approve…</button>
              <button type="button" onClick={() => setMode('reject')} className="text-xs border border-red-800 text-red-400 rounded px-2 py-1 hover:bg-red-900/40">Reject…</button>
            </>
          ) : null}
          {advance.status === 'pending' ? (
            <button type="button" onClick={() => disburse.mutate()} className="text-xs border border-gray-700 rounded px-2 py-1 hover:bg-gray-900">Confirm disbursement</button>
          ) : null}
          {advance.status === 'disbursed' || advance.status === 'active' ? (
            <button type="button" onClick={() => forgive.mutate()} className="text-xs border border-red-800 text-red-400 rounded px-2 py-1 hover:bg-red-900/40">Forgive</button>
          ) : null}
        </div>
      </div>

      {mode === 'approve' ? (
        <ApproveForm request={advance} onDone={() => { setMode('none'); onChanged(); }} onCancel={() => setMode('none')} />
      ) : null}
      {mode === 'reject' ? (
        <RejectForm requestId={advance.id} onDone={() => { setMode('none'); onChanged(); }} onCancel={() => setMode('none')} />
      ) : null}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <label className="flex flex-col gap-1 text-xs text-gray-400">
      {label}
      {children}
    </label>
  );
}
